import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { tournamentApi } from "@bw/shared/api";
import { formatMoney } from "@bw/shared/utils";
import type { TournamentRegistrationPaymentResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import { useAbandonRegistrationAttempt } from "../hooks/useAbandonRegistrationAttempt";
import { useResumeRegistrationCheckout } from "../hooks/useResumeRegistrationCheckout";
import { GuardianCardSetupRecovery } from "./GuardianCardSetupRecovery";

export function TournamentRegistrationPayments() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const paymentId = params.get("payment_id") || undefined;
  const rawPage = Number(params.get("payment_page"));
  const page =
    !paymentId && Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const payments = useQuery({
    queryKey: [
      ...queryKeys.tournaments.myRegistrationPayments(),
      user?.uid,
      { page, paymentId },
    ],
    queryFn: () =>
      tournamentApi.getMyRegistrationPayments({
        limit: 20,
        offset: (page - 1) * 20,
        ...(paymentId ? { payment_id: paymentId } : {}),
      }),
    enabled: Boolean(user),
  });
  function changePage(value: number) {
    const next = new URLSearchParams(params);
    next.set("payment_page", String(value));
    setParams(next);
  }
  if (!user) return null;
  // Nothing to act on: stay quiet rather than a heading over an empty line.
  if (
    !paymentId &&
    page === 1 &&
    payments.isSuccess &&
    !payments.data?.items.length
  )
    return null;
  return (
    <Stack
      component="section"
      aria-label="Registration payments"
      spacing={1.5}
      sx={{ mb: 3 }}
    >
      <Typography component="h2" variant="h6">
        Registration payments
      </Typography>
      {paymentId ? (
        <Button
          sx={{ alignSelf: "flex-start" }}
          onClick={() => {
            const next = new URLSearchParams(params);
            next.delete("payment_id");
            next.delete("payment_page");
            setParams(next);
          }}
        >
          All payments
        </Button>
      ) : null}
      {paymentId ? (
        <Button
          sx={{ alignSelf: "flex-start" }}
          disabled={payments.isFetching}
          onClick={() => void payments.refetch()}
        >
          Refresh status
        </Button>
      ) : null}
      {payments.isLoading ? (
        <Typography role="status">Loading registration payments…</Typography>
      ) : payments.isError ? (
        <Alert
          severity="error"
          action={
            <Button onClick={() => void payments.refetch()}>Retry</Button>
          }
        >
          Could not load your registration payments.
        </Alert>
      ) : !payments.data?.items.length ? (
        <Typography color="text.secondary">
          {paymentId
            ? "This payment attempt is unavailable."
            : "No registration payments requiring attention on this page."}
        </Typography>
      ) : (
        <List disablePadding component={Paper} variant="outlined">
          {payments.data.items.map((payment) => (
            <PaymentRow
              key={`${user.uid}:${payment.registration_checkout_id}`}
              payment={payment}
              refreshing={payments.isFetching}
            />
          ))}
        </List>
      )}
      {payments.data &&
      !payments.isError &&
      (page > 1 || payments.data.has_more) ? (
        <Stack direction="row" spacing={1}>
          <Button disabled={page === 1} onClick={() => changePage(page - 1)}>
            Previous payments
          </Button>
          <Button
            disabled={!payments.data.has_more}
            onClick={() => changePage(page + 1)}
          >
            Next payments
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}

function PaymentRow({
  payment,
  refreshing,
}: {
  payment: TournamentRegistrationPaymentResponse;
  refreshing: boolean;
}) {
  const resolve = useAbandonRegistrationAttempt(
    payment.tournament_id,
    payment.registration_checkout_id,
  );
  const lock = useRef(false);
  const resume = useResumeRegistrationCheckout();
  const mounted = useRef(true);
  const [navigationError, setNavigationError] = useState(false);
  const [cardSetupOpen, setCardSetupOpen] = useState(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const pending = resolve.isPending || resume.isPending || refreshing;
  async function resumeCheckout() {
    if (
      lock.current ||
      pending ||
      !payment.can_resume_checkout ||
      payment.kind !== "hosted"
    )
      return;
    lock.current = true;
    setNavigationError(false);
    try {
      const response = await resume.mutateAsync(
        payment.registration_checkout_id,
      );
      if (!mounted.current) return;
      const target = new URL(response.checkout_url);
      if (
        target.origin !== "https://checkout.stripe.com" ||
        target.username ||
        target.password
      ) {
        setNavigationError(true);
        return;
      }
      window.location.assign(target.href);
    } catch {
      if (mounted.current) setNavigationError(true);
    } finally {
      lock.current = false;
    }
  }
  async function submit() {
    if (
      lock.current ||
      pending ||
      !payment.can_resolve ||
      payment.kind !== "direct_charge"
    )
      return;
    lock.current = true;
    try {
      await resolve.mutateAsync(payment.draw_id);
    } catch {
      /* The safe error and reloaded source describe uncertainty. */
    } finally {
      lock.current = false;
    }
  }
  const kind = {
    direct_charge: "Card payment",
    guardian_card_setup: "Guardian card setup",
    hosted: "Hosted checkout",
  }[payment.kind];
  return (
    <ListItem divider sx={{ display: "block", py: 2 }}>
      <Stack
        spacing={1}
        sx={{ alignItems: "flex-start", overflowWrap: "anywhere" }}
      >
        <Typography sx={{ fontWeight: 700 }}>
          {payment.tournament_name}
        </Typography>
        <Typography variant="body2">
          {payment.event_name} · {payment.draw_name}
        </Typography>
        <Typography variant="body2">
          {kind} · {formatMoney(payment.amount, payment.currency)}
        </Typography>
        <Typography variant="body2">
          Attempt: <span>{payment.status.replaceAll("_", " ")}</span>
        </Typography>
        <Typography variant="body2">
          Payment: {payment.payment_status.replaceAll("_", " ")}
        </Typography>
        <Typography variant="body2">
          Refund: {payment.refund_status.replaceAll("_", " ")}
        </Typography>
        {payment.awaiting_guardian_consent ? <Typography>Awaiting guardian approval. No entry fee is charged by card setup.</Typography> : null}
        {payment.kind === "guardian_card_setup" && !payment.awaiting_guardian_consent && (payment.can_resume_card_setup || payment.can_release_card_setup) ? (
          cardSetupOpen ? <GuardianCardSetupRecovery payment={payment} /> : <Button disabled={pending} onClick={() => setCardSetupOpen(true)}>Continue card setup</Button>
        ) : null}
        {payment.can_resume_checkout && payment.kind === "hosted" ? (
          <Button disabled={pending} onClick={() => void resumeCheckout()}>
            {resume.isPending ? "Opening checkout…" : "Resume checkout"}
          </Button>
        ) : null}
        {navigationError || resume.isError ? (
          <Alert severity="error">
            Could not resume checkout. Refresh the payment status and try again.
          </Alert>
        ) : null}
        {payment.can_resolve && payment.kind === "direct_charge" ? (
          <>
            <Typography variant="body2" color="text.secondary">
              Checks the original payment. If money was captured, it completes
              the registration or refunds it when completion is no longer
              possible. Otherwise it releases the unpaid attempt. No new charge
              is made.
            </Typography>
            <Button disabled={pending} onClick={() => void submit()}>
              {resolve.isPending ? "Resolving…" : "Resolve payment"}
            </Button>
          </>
        ) : null}
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button component={RouterLink} to={payment.detail_url}>
            View payment
          </Button>
          {payment.entry_url ? (
            <Button component={RouterLink} to={payment.entry_url}>
              View entry
            </Button>
          ) : null}
        </Stack>
        {resolve.isError ? (
          <Alert severity="error">
            Could not confirm the payment resolution. Review the latest status
            before trying again.
          </Alert>
        ) : null}
      </Stack>
    </ListItem>
  );
}
